import { computed, type ShallowRef } from 'vue';
import { isString } from '@cvo/domain';
import { isFixedCartItem, type AllowedCartItem, type CartItemTypes, type PercentageCartItem, type FixedCartItem, type DefaultCartMeta } from '../types/cart';

/**
 * A composable that encapsulates all cart calculation logic.
 * It calculates the total amount for the cart and for individual items.
 *
 * @param items - A ref to the array of cart items.
 * @returns An object with the total and calculation functions.
 */
export function useCartCalculator<TExtra extends DefaultCartMeta = DefaultCartMeta>(items: ShallowRef<Array<AllowedCartItem<TExtra>>>) {
  const total = computed(() => {
    return items.value.reduce((acc, item) => acc + calculateItemTotalAmount(item), 0);
  });

  /** Helper function to get total amount for a single item */
  function calculateItemTotalAmount(item: AllowedCartItem<TExtra>): number {
    const calculator = totalAmountCalculatorPerType[item.type];
    // @ts-expect-error - This is a known TS limitation with discriminated unions in complex maps.
    return calculator(item) ?? 0;
  }

  // ===== Helper functions ===== //

  function getItemsFromGroup(group: string): Array<AllowedCartItem<TExtra>> {
    return items.value.filter((item) => item.group === group);
  }

  /**
   * Calculates the subtotal for a given group, only considering fixed items.
   * @param group The cart item group.
   * @param filterFct Optional filter function that receives the extended cart item type.
   * @returns The subtotal of the group.
   */
  function calculateFixedSubtotalByGroup(
    group: string,
    filterFct?: (item: AllowedCartItem<TExtra>) => boolean,
  ): number {
    return getItemsFromGroup(group)
      .filter((item): item is FixedCartItem<TExtra> => {
        return isFixedCartItem(item) && (filterFct ? filterFct(item) : true);
      })
      .reduce((sum, item) => sum + calculateFixedItemAmount(item), 0);
  }

  /** Total amount for a fixed item */
  function calculateFixedItemAmount(item: FixedCartItem<TExtra>): number {
    if (isString(item.value)) return 0;
    return item.value * (item.quantity || 1);
  }

  /** Total amount for a percentage item */
  function calculatePercentageItemAmount(item: PercentageCartItem<TExtra>): number {
    const subtotal = calculateFixedSubtotalByGroup(item.baseGroup);
    return subtotal * item.rate;
  }

  const totalAmountCalculatorPerType = {
    fixed: calculateFixedItemAmount,
    percentage: calculatePercentageItemAmount,
  } satisfies {
    [K in CartItemTypes]: (item: Extract<AllowedCartItem<TExtra>, { type: K }>) => number;
  };

  return {
    total,
    calculateFixedSubtotalByGroup,
    calculateItemTotalAmount,
  };
}
